import { FetchResult } from '@apollo/client';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import {
  AuthToken,
  ForgotPasswordInput,
  ResetPasswordInput,
} from '../../graphql.generated';
import paths from '../../paths.json';
import { onUnauthorized, setAuthorization } from '../GraphQLProvider/authLink';
import Loading from '../Loading';

import {
  ForgotPasswordMutation,
  useForgotPasswordMutation,
} from './ForgotPassword.generated';
import { LoginMutation, useLoginMutation } from './Login.generated';
import { useLogoutMutation } from './Logout.generated';
import {
  RefreshTokenMutation,
  useRefreshTokenMutation,
} from './RefreshToken.generated';
import {
  ResetPasswordMutation,
  useResetPasswordMutation,
} from './ResetPassword.generated';

// Public forms render errors inline, so they opt out of the global notification.
const inlineErrors = { context: { skipErrorNotification: true } };

type LogoutOptions = {
  /** Skip the server-side `logout` call, e.g. when the session is already revoked. */
  local?: boolean;
  /** Router state for the login page (`success` / `error` message). */
  state?: { success?: string; error?: string };
};

type Authentication = {
  accessToken?: string;
  authenticated: boolean;
  forgotPassword: (
    input: ForgotPasswordInput,
  ) => Promise<FetchResult<ForgotPasswordMutation>>;
  resetPassword: (
    input: ResetPasswordInput,
  ) => Promise<FetchResult<ResetPasswordMutation>>;
  login: (
    username: string,
    password: string,
  ) => Promise<FetchResult<LoginMutation>>;
  logout: (options?: LogoutOptions) => void;
  refreshToken?: string;
  renewToken: () => Promise<FetchResult<RefreshTokenMutation>>;
};

type AuthenticationState = Pick<AuthToken, 'accessToken' | 'refreshToken'> & {
  expiresAt: number;
};

const contextError = () => {
  throw new Error('No <AuthenticationProvider />');
};

const contextErrorPromise = () => Promise.reject(contextError());

const AuthenticationContext = createContext<Authentication>({
  authenticated: false,
  forgotPassword: contextErrorPromise,
  login: contextErrorPromise,
  logout: () => undefined,
  renewToken: contextErrorPromise,
  resetPassword: contextErrorPromise,
});

export const useAuthentication = (): Authentication =>
  useContext(AuthenticationContext);

const getExpiresAt = (expiresIn: number) =>
  new Date(Date.now() + expiresIn * 1e3).getTime();

const isValid = (authentication?: AuthenticationState) =>
  !!authentication && Date.now() <= authentication.expiresAt;

const AuthenticationProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const history = useHistory();

  const [authentication, setAuthenticationState] = useState<
    AuthenticationState | undefined
  >(() => {
    const item = localStorage.getItem('authentication');
    if (!item) {
      return;
    }

    try {
      return JSON.parse(item) as AuthenticationState;
    } catch (e) {
      return undefined;
    }
  });

  // Renew one hour before the access token expires (immediately if it already did).
  const renewIn = useMemo(
    () =>
      authentication?.refreshToken
        ? Math.max(0, authentication.expiresAt - Date.now() - 36e5)
        : undefined,
    [authentication],
  );

  const setAuthentication = useCallback(
    (result: Pick<AuthToken, 'accessToken' | 'expiresIn' | 'refreshToken'>) => {
      setAuthenticationState({
        accessToken: result.accessToken,
        expiresAt: getExpiresAt(result.expiresIn),
        refreshToken: result.refreshToken,
      });
    },
    [],
  );

  const [_login] = useLoginMutation({
    ...inlineErrors,
    onCompleted: (data) => {
      const token = data.login?.token;
      if (token) {
        setAuthentication(token);
      }
    },
  });
  const [_logout] = useLogoutMutation();
  const [_forgotPassword] = useForgotPasswordMutation(inlineErrors);
  const [_resetPassword] = useResetPasswordMutation(inlineErrors);
  const [_renewToken, { called: renewTokenCalled, loading: refreshing }] =
    useRefreshTokenMutation({
      ...inlineErrors,
      onCompleted: (data) => {
        // Refresh tokens rotate: the response carries the one to use next time.
        const token = data.refreshToken?.token;
        if (token) {
          setAuthentication(token);
        }
      },
      onError: () => {
        // Revoked, expired or reused refresh token: the session is over.
        setAuthenticationState(undefined);
      },
    });

  const timeoutRef = useRef<number | null>(null);

  const authenticated = useMemo(
    () => isValid(authentication),
    [authentication],
  );

  const login = useCallback(
    (username: string, password: string) =>
      _login({ variables: { input: { password, username } } }),
    [_login],
  );

  const forgotPassword = useCallback(
    (input: ForgotPasswordInput) => _forgotPassword({ variables: { input } }),
    [_forgotPassword],
  );

  const resetPassword = useCallback(
    (input: ResetPasswordInput) => _resetPassword({ variables: { input } }),
    [_resetPassword],
  );

  const logout = useCallback(
    ({ local = false, state }: LogoutOptions = {}) => {
      const refreshToken = authentication?.refreshToken;

      if (refreshToken && !local) {
        // Best effort: revoke the refresh token server-side, but never block logout on it.
        _logout({ variables: { input: { refreshToken } } }).catch(console.warn);
      }

      setAuthorization(undefined);
      setAuthenticationState(undefined);
      history.push(paths.login, state);
    },
    [_logout, authentication?.refreshToken, history],
  );

  // The API rejected our token (401): drop the session without calling logout.
  useEffect(
    () =>
      onUnauthorized(() => {
        setAuthorization(undefined);
        setAuthenticationState(undefined);
      }),
    [],
  );

  const renewToken = useCallback(() => {
    timeoutRef.current = null;

    if (authentication?.refreshToken) {
      return _renewToken({
        variables: { input: { refreshToken: authentication.refreshToken } },
      });
    }

    return Promise.reject(new Error('No Refresh Token'));
  }, [_renewToken, authentication?.refreshToken]);

  useEffect(() => {
    if (!authentication) {
      localStorage.removeItem('authentication');

      return;
    }

    localStorage.setItem('authentication', JSON.stringify(authentication));

    if (renewIn === undefined) {
      return;
    }

    timeoutRef.current = window.setTimeout(
      () => void renewToken().catch(console.warn),
      renewIn,
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [authentication, renewIn, renewToken]);

  // Set synchronously during render (not in an effect) so that queries started
  // by children on their first render already carry the token.
  setAuthorization(authenticated ? authentication?.accessToken : undefined);

  // Wait until refresh token finishes
  if ((!renewTokenCalled && renewIn === 0) || refreshing) {
    return <Loading />;
  }

  return (
    <AuthenticationContext.Provider
      value={{
        accessToken: authentication?.accessToken,
        authenticated,
        forgotPassword,
        login,
        logout,
        refreshToken: authentication?.refreshToken ?? undefined,
        renewToken,
        resetPassword,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

AuthenticationProvider.displayName = 'AuthenticationProvider';

export default AuthenticationProvider;
